import React, { useContext, useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

import { AuthContext } from 'app/shared/context/Auth';
import { useCheckoutEvent } from 'app/fan/utils/checkout';

interface StripeElementsProviderProps {
  eventId?: string;
}

export const StripeElementsProvider: React.FC<
  React.PropsWithChildren<StripeElementsProviderProps>
> = ({ eventId, children }) => {
  const { user: loggedInUser, loggedIn } = useContext(AuthContext);

  const { event } = useCheckoutEvent(eventId);

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  useEffect(() => {
    /*
      Some context:

        1- Events have a default billing region based on their country (e.g. all
          London events have a GB billing region by default). Same with publishable
          keys.
        2- Users have a billing region set once they make a first purchase: if
          their first purchase is a UK event, we create a customer on our UK
          Stripe account and store their Stripe details on the User table on our
          db. If their first purchase is a US event, we create a customer on our
          US Stripe account and store this info on User table.
        3- If a user with UK billing region goes through checkout for a US event,
          the Event's default billing region (US) is overridden by the user's
          billing region. This is done on sofar-main, on Event Type. If user is not
          logged in, the billing region and publishable key used is the event default
          billing region.
        4- When a user is not logged in and goes through checkout for a US event,
          the event's billing region is US. If we initialize Stripe with our US
          Stripe account publishable key, and then this user goes through the auth stage
          and signs in with an account that has a GB billing region,
          we'll run into a mismatch between the client's Stripe account and the
          server's Stripe account being used. Since we can't update Stripe's initialize info
          once we load Stripe on the client, we should only load Stripe once we're 100%
          sure what the billing region and publishable key should be.

          This solves issues like 'No such payment method' or 'No such payment intent', use of
          saved cards, etc.
    */

    const publishableKey = event && event.billingPublishableKey;
    const eventBillingRegion = event && event.billingRegion;
    const userBillingRegion = loggedInUser?.billingRegion;

    // If these match it means user is logged in and event billing region and publishable
    // key match the user's billing region
    const eventAndUserBillingRegionMatch =
      eventBillingRegion === userBillingRegion;

    // User is logged in but has no billing region (i.e. they have never purchased tickets yet,
    // so we haven't stored their Stripe details on User table)
    const userIsLoggedInAndHasNoBillingRegion =
      loggedIn && loggedInUser && !loggedInUser.billingRegion;

    const userIsNotLoggedIn = !loggedIn && !loggedInUser;

    if (
      publishableKey &&
      (eventAndUserBillingRegionMatch ||
        userIsLoggedInAndHasNoBillingRegion ||
        userIsNotLoggedIn)
    ) {
      const stripePromise = loadStripe(publishableKey);
      setStripePromise(stripePromise);
    }
  }, [event, loggedIn, loggedInUser]);

  return <Elements stripe={stripePromise}>{children}</Elements>;
};
